/**
 * Один элемент закрепа (pin) над чатом Activity.
 * Показывает визуальное состояние пина и маршрутизирует клик/действия
 * наружу через `onClick` и `onAction`.
 */
import { memo, useState } from 'react';
import { ActionIcon, Box, Group, Menu, Text } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import type { ActivityChatPin } from '@ayocore/activity/common/types/activity-pin';
import { DEFAULT_ACTIVITY_PIN_COLOR_PAIR } from '@ayocore/activity/common/activity/pin-colors';

type ActivityPinItemProps = {
  pin: ActivityChatPin;
  onClick?: (pin: ActivityChatPin) => void;
  onAction?: (pin: ActivityChatPin, actionId: string) => void;
};

function ActivityPinItemComponent({ pin, onClick, onAction }: ActivityPinItemProps) {
  const background = pin.color || DEFAULT_ACTIVITY_PIN_COLOR_PAIR.background;
  const textColor = pin.textColor || DEFAULT_ACTIVITY_PIN_COLOR_PAIR.text;
  const prefixBadge = pin.prefix_badge;
  const showOverdueCircle = prefixBadge?.variant === 'overdue';
  const showSelfMarker = prefixBadge?.variant === 'self';
  const hasActions = Boolean(pin.actions && pin.actions.length > 0);
  const [menuOpened, setMenuOpened] = useState(false);

  return (
    <Box
      key={`pin-${pin.id}`}
      component='div'
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? () => onClick(pin) : undefined}
      onContextMenu={
        hasActions
          ? event => {
              event.preventDefault();
              setMenuOpened(true);
            }
          : undefined
      }
      onKeyDown={
        onClick
          ? event => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onClick(pin);
            }
          : undefined
      }
      style={{
        maxWidth: '100%',
        borderRadius: 999,
        background,
        padding: '4px 10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Group gap='xs' justify='space-between' wrap='nowrap'>
        <Group gap={6} wrap='nowrap' style={{ minWidth: 0 }}>
          {showSelfMarker ? (
            <Box
              style={{
                minWidth: 16,
                height: 16,
                paddingInline: 4,
                borderRadius: 999,
                background: 'var(--mantine-color-red-6)',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              title='Моя задача'
            >
              {prefixBadge?.text}
            </Box>
          ) : null}
          {showOverdueCircle ? (
            <Box
              style={{
                minWidth: 16,
                height: 16,
                paddingInline: 4,
                borderRadius: 999,
                background: 'var(--mantine-color-red-6)',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              title={`${prefixBadge?.text ?? ''} дней просрочки`}
            >
              {prefixBadge?.text}
            </Box>
          ) : null}
          <Text
            size='xs'
            style={{
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 360,
              lineHeight: '24px',
              color: textColor,
            }}
            title={pin.title}
          >
            {pin.title}
          </Text>
        </Group>
        {hasActions ? (
          <Menu
            withArrow
            position='bottom-end'
            withinPortal
            opened={menuOpened}
            onChange={setMenuOpened}
          >
            <Menu.Target>
              <ActionIcon
                variant='subtle'
                size='xs'
                onClick={event => event.stopPropagation()}
                aria-label='Действия'
                style={{ color: textColor }}
              >
                <IconDots size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {(pin.actions ?? []).map(action => (
                <Menu.Item
                  key={`${pin.id}:${action.id}`}
                  color={action.color === 'red' ? 'red' : undefined}
                  onClick={event => {
                    event.stopPropagation();
                    onAction?.(pin, action.id);
                  }}
                >
                  {action.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </Group>
    </Box>
  );
}

export const ActivityPinItem = memo(
  ActivityPinItemComponent,
  (prev, next) => prev.pin === next.pin
);
